import { keyBy, get, head, debounce, flatten } from 'lodash';
import immer from 'immer';
import heydoctor from 'app/vendor/heydoctor';
import { sessionStorage } from 'app/lib/storage';
import { Stores, Store } from 'app/lib/synaptik';
import { RequestedDrug } from 'app/types';
import { REQUESTED_DRUG_KEY } from 'app/stores/services';

const DRUG_PREFERENCE_QUESTION_CODE = 'preference';

// use answer id or label as value if not available
const getIdOrLabel = (answer: any) => answer.id || answer.label;

// some answers need to use an array like checkbox or if there is more then one value
const shouldUseArray = (answer: any) => ['checkbox'].includes(answer.type) || answer.length > 1;

// set the answers
const getExistingAnswers = (savedAnswers: any, localCachedAnswers: any, prepopulatedAnswers?: any) => {
  if (!savedAnswers) {
    return { ...prepopulatedAnswers, ...localCachedAnswers };
  }

  return {
    ...prepopulatedAnswers,
    ...keyBy(
      savedAnswers.map((answer: any) => ({
        id: answer.id,
        value: shouldUseArray(answer)
          ? answer.answers.map((a: any) => getIdOrLabel(a))
          : getIdOrLabel(head(answer.answers)),
      })),
      'id'
    ),
  };
};

const getPrepopulatedAnswers = (questions: any[], requestedDrug?: RequestedDrug, serviceCode?: string) => {
  const drugPreferenceQuestion: any = flatten(questions.map((q: { children: any[] }) => [q, ...q.children])).find(
    (q: any) => q.code === DRUG_PREFERENCE_QUESTION_CODE
  );

  if (drugPreferenceQuestion && requestedDrug) {
    return {
      [drugPreferenceQuestion.id]: {
        value: `${requestedDrug.displayName} ${requestedDrug.dose}, ${requestedDrug.quantity} pills`,
        trigger: drugPreferenceQuestion.trigger,
      },
      [drugPreferenceQuestion.trigger.parentQuestionId]: {
        value: drugPreferenceQuestion.trigger.qnrChoiceId,
      },
    };
  }

  const drug: { display_name: string; service: string } = sessionStorage.get(REQUESTED_DRUG_KEY);

  if (drugPreferenceQuestion && drug && drug?.service === serviceCode && serviceCode === 'ED') {
    return {
      [drugPreferenceQuestion.id]: {
        value: drug.display_name,
        trigger: drugPreferenceQuestion.trigger,
      },
      [drugPreferenceQuestion.trigger.parentQuestionId]: {
        value: drugPreferenceQuestion.trigger.qnrChoiceId,
      },
    };
  }

  return;
};

const Validations = {
  required(value: any) {
    if (
      value === undefined ||
      value === null ||
      (typeof value === 'string' && !value.trim()) ||
      (Array.isArray(value) && !value.length)
    ) {
      return 'Please answer this question.';
    }
    return null;
  },
};

const initialState = {
  errors: {},
  questions: [],
  questionnaire: {},
  answers: {},
};

interface State {
  questions: any[];
  errors: Record<string, any>;
  questionnaire: Record<string, any>;
  answers: Record<string, any>;
}

export class QuestionnaireStore extends Store<State, Stores> {
  state: State = { ...initialState };

  fetch = async (visitId: any) =>
    this.runAsync({
      key: 'fetchingQuestionnaire',
      work: async () => {
        const questionnaire = await heydoctor.visits.fetchQuestionnaire(visitId);

        const requestedDrug = this.stores.visits.state.visit?.requestedDrug;
        const serviceCode = this.stores.visits.state.visit?.service?.code;
        const cachedAnswers = this.retrieveCachedAnswers();

        this.setState({
          questionnaire,
          questions: questionnaire.questions,
          answers: getExistingAnswers(
            questionnaire?.response?.snapshot?.questions,
            cachedAnswers,
            getPrepopulatedAnswers(questionnaire.questions, requestedDrug, serviceCode)
          ),
        });
      },
    } as any);

  isQuestionnaireComplete = () => this.state.questions.every(this.isQuestionComplete);

  getQuestionAnswer = (questionId: any) => get(this.state, `answers.${questionId}`);

  updateQuestionAnswer = debounce(async ({ question, value }) => {
    const { answers } = this.state;

    this.setState({
      answers: {
        ...answers,
        [question.id]: {
          value,
          trigger: question.trigger,
        },
      },
    });

    this.cacheAnswers(this.state.answers);

    // validate question after answer updated
    this.validateQuestion(question);
  }, 25);

  getCacheKey = () => {
    const { id } = this.stores.visits.state.visit!;
    return `visit-${id}-answers`;
  };

  cacheAnswers = (answers: any) => sessionStorage.set(this.getCacheKey(), answers);

  retrieveCachedAnswers = () => {
    return this.state.answers;
  };

  deleteQuestionnaireCache = () => sessionStorage.remove(this.getCacheKey());

  getFormattedAnswers = () => {
    const { answers } = this.state;

    return Object.entries(answers)
      .filter(([, answer]: any) => !!answer.value && this.isChildQuestionVisible({ trigger: answer.trigger }))
      .map(([id, answer]: any) => ({
        question_id: id,
        trigger: answer.trigger,
        value: answer.value,
      }));
  };

  validateQuestion = (question: any) => {
    this.setState(
      immer((state) => {
        state.errors![question.id] = this.buildQuestionError(question);
      })
    );
  };

  validateAllQuestions = async () => {
    // get all questions currently visible
    const visibleQuestions = this.state.questions
      .filter((q) => this.isQuestionVisible(q.id))
      .reduce((arr, q) => [...arr, q, ...q.children.filter((ques: any) => this.isChildQuestionVisible(ques))], []);

    // determine which visible questions are invalid and build error object
    const errors = visibleQuestions
      .filter((q: any) => this.isQuestionInvalid(q))
      .map((q: any) => this.buildQuestionError(q));

    // update errors keying by question id
    this.setState({ errors: keyBy(errors, 'id') });
  };

  buildQuestionError = (question: any) => ({
    id: question.id,
    error: this.isQuestionInvalid(question),
    parentQuestionId: get(question, 'trigger.parentQuestionId', question.id),
  });

  getNextError = () => head(Object.values(this.state.errors));

  isQuestionVisible = (questionId: any) => {
    const { questions } = this.state;
    if (!questions) return false;

    const prevQuestions = questions.filter((q: any) => q.id < questionId && q.id !== questionId);

    // First question
    if (prevQuestions.length === 0) return true;

    return prevQuestions.every(this.isQuestionComplete);
  };

  isQuestionComplete = (question: any) => {
    const rootQuestionValid = !this.isQuestionInvalid(question);
    const incompleteChildren = (question.children || []).some(
      (childQuestion: any) => this.isChildQuestionVisible(childQuestion) && !this.isQuestionComplete(childQuestion)
    );

    return rootQuestionValid && !incompleteChildren;
  };

  isQuestionInvalid = (question: any) => {
    let error: any = false;
    const { id, validations } = question;

    try {
      const { value } = this.getQuestionAnswer(id);

      if (validations.required) {
        error = Validations.required(value);
      }
    } catch (e) {
      error = 'Please answer this question.';
    }

    return error;
  };

  isChildQuestionVisible = (question: any) => {
    const { trigger } = question;
    // Root question
    if (!trigger) return true;

    try {
      const { value } = this.getQuestionAnswer(trigger.parentQuestionId);

      if (Array.isArray(value)) {
        return value.includes(trigger.qnrChoiceId);
      }

      if (value === trigger.qnrChoiceId) {
        return true;
      }

      return false;
    } catch (err) {
      return false;
    }
  };

  reset = () => {
    this.setState(initialState);
  };
}
